import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { User } from '../data';
import { canAccess, homePathFor } from '../core/access';
import { resolveRoutes } from '../company/pages';
import { getBootCompanyId, isScreenEnabled } from '../company/session';
import RouteError from '../components/route-error/RouteError';

/**
 * Returns the default landing path for a user who is denied a route.
 * Clients land on their own company profile (or stores); everyone else on the
 * dashboard, which every non-client role can access.
 */
function defaultPathFor(user) {
    return homePathFor(user);
}

/**
 * Guards a route behind the central access model (see core/access.js).
 * Redirects users who lack access for `navKey` to their default landing page.
 *
 * A screen the signed-in company does not have redirects home even before the user is known.
 * While the auth check is still in flight the user singleton is null; otherwise we render
 * children rather than redirect (the page's controller performs authentication), matching the
 * app's existing hard-refresh behavior.
 */
function RequireAccess({ navKey, children }) {
    const user = User.getInstance();
    if (!isScreenEnabled(navKey)) return <Navigate to={defaultPathFor(user)} replace />;
    if (!user) return children;
    if (!canAccess(user.userType, navKey)) {
        return <Navigate to={defaultPathFor(user)} replace />;
    }
    return children;
}

/** A base route the signed-in company removed: its URL sends the user home. */
function RedirectHome() {
    return <Navigate to={defaultPathFor(User.getInstance())} replace />;
}

function routeElement(route) {
    const Page = route.component;
    return route.guard ? (
        <RequireAccess navKey={route.guard}>
            <Page />
        </RequireAccess>
    ) : (
        <Page />
    );
}

/**
 * The route table of the company the page was loaded for (company/pages.js): base routes minus
 * the company's `remove`, its page overrides, plus its `extraRoutes`. Signing in to another
 * company reloads the page, so the table is built once.
 */
function buildRouter() {
    const { routes, removed } = resolveRoutes(getBootCompanyId());
    // Every route gets a friendly error screen, and unknown URLs a "not found" page.
    return createBrowserRouter([
        ...routes.map((route) => ({ path: route.path, element: routeElement(route), errorElement: <RouteError /> })),
        ...removed.map((route) => ({ path: route.path, element: <RedirectHome />, errorElement: <RouteError /> })),
        { path: '*', element: <RouteError notFound /> }
    ]);
}

let router = null;

export default function ShelfRouter() {
    if (!router) router = buildRouter();
    return <RouterProvider router={router} />;
}
